import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import CmsGallery from '../components/CmsGallery';
import ContactForm from '../components/ContactForm';
import { getOptimizedImageUrl } from '../lib/cms';

export default function AkutAnbringelse() {
  const experiencedWith = [
    'Udadreagerende og uhensigtsmæssig adfærd',
    'Selvskadende adfærd',
    'Personlighedsforstyrrelser',
    'Misbrug',
    'Omsorgssvigt',
    'Autisme og ADHD',
    'Krænkende adfærd',
    'Socialt udsatte borgere i akut krise',
  ];

  return (
    <>
      <SEO
        title="Akut anbringelse - Akut Vikaren ApS"
        description="Når der skal handles hurtigt: Akut anbringelse (jf. Barnets lov § 12, stk. 3 / Servicelovens § 4, stk. 4) med kort responstid, døgnet rundt. Vi modtager borgere fra hele landet."
        ogImage="https://assets.auxodev.dk/assets/001da5eb-368a-49cd-a912-65694d60b551/image00006.webp"
      />

      {/* Hero Header */}
      <section className="relative bg-[#041c2c] text-white py-16 sm:py-24 border-b border-slate-800 overflow-hidden">
        {/* Background image with dark transparent overlay */}
        <div className="absolute inset-0">
          <img
            src="https://assets.auxodev.dk/assets/001da5eb-368a-49cd-a912-65694d60b551/image00006.webp"
            alt="Akut anbringelse – omsorg og tryghed for børn og unge"
            className="w-full h-full object-cover"
            style={{ objectPosition: 'center 40%' }}
            loading="eager"
            fetchPriority="high"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-r from-[#041c2c]/90 via-[#041c2c]/75 to-[#041c2c]/55" />

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl space-y-4">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-slate-800/80 border border-slate-700/80 text-xs font-semibold text-[#b39366] uppercase tracking-wider">
              <i className="fa-solid fa-shield-halved text-xs" aria-hidden="true"></i>
              <span>Akut Ydelse • Landsdækkende</span>
            </div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white tracking-tight leading-tight">
              Akut anbringelse
            </h1>
            <p className="text-slate-300 text-lg sm:text-xl font-normal leading-relaxed">
              Når der skal handles nu
            </p>
          </div>
        </div>
      </section>

      {/* Akut Emergency Direct Call Banner */}
      <div className="bg-[#062438] border-b border-slate-800 text-white py-4 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3 text-center sm:text-left">
            <div className="w-9 h-9 rounded-full bg-[#b39366]/20 text-[#b39366] flex items-center justify-center shrink-0">
              <i className="fa-solid fa-phone-volume text-sm" aria-hidden="true"></i>
            </div>
            <div>
              <p className="text-xs font-semibold text-[#b39366] uppercase tracking-wider">Akut telefon 24/7</p>
              <p className="text-sm font-medium text-slate-200">Har I en akut opgave, der skal løses nu?</p>
            </div>
          </div>

          <a
            href="tel:+4523964042"
            className="inline-flex items-center gap-2 bg-[#b39366] hover:bg-[#a18155] text-white px-5 py-2.5 rounded text-sm font-bold shadow transition-all whitespace-nowrap"
          >
            <i className="fa-solid fa-phone text-xs" aria-hidden="true"></i>
            <span className="whitespace-nowrap">Ring nu: (+45) 23 96 40 42</span>
          </a>
        </div>
      </div>

      {/* Main Content */}
      <div className="bg-white py-14 sm:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            {/* Main Column */}
            <div className="lg:col-span-8 space-y-12">
              {/* Sektion 1: Når der skal handles nu */}
              <div className="space-y-4">
                <div className="p-4 rounded-xl bg-slate-50 border border-slate-200 text-xs text-slate-600 font-medium leading-relaxed">
                  Lovgrundlag: Jf. Barnets lov § 12, stk. 3, for borgere under 18 år, og Servicelovens § 4, stk. 4, for borgere over 18 år.
                </div>
                <p className="text-slate-700 text-base sm:text-lg leading-relaxed pt-2">
                  Nogle situationer kan ikke vente. Akutvikaren står klar med akut anbringelse (jf. Barnets lov § 12, stk. 3, for borgere under 18 år, og Servicelovens § 4, stk. 4, for borgere over 18 år), når en borger med kort varsel har brug for et trygt sted at være. Vi modtager borgere fra hele landet, uanset tidspunkt på døgnet.
                </p>
              </div>

              {/* Sektion 2: Hvad er akut anbringelse? */}
              <div className="space-y-4">
                <h2 className="text-2xl sm:text-3xl font-bold text-[#041c2c] tracking-tight">
                  Hvad er akut anbringelse?
                </h2>
                <p className="text-slate-700 text-base sm:text-lg leading-relaxed">
                  Akut anbringelse handler om at skabe et trygt og stabilt sted at være for en borger, der pludselig står uden et hjem eller en tryg base – for eksempel som følge af en akut opstået krise, sammenbrud i hjemmet eller andre alvorlige situationer. Vores medarbejdere har erfaring med at gå ind i uforudsete og ofte følelsesmæssigt belastede situationer med ro, tryghedsskabende faglighed og overblik.
                </p>
              </div>

              {/* Sektion 3: Kort responstid */}
              <div className="space-y-4">
                <h2 className="text-2xl sm:text-3xl font-bold text-[#041c2c] tracking-tight">
                  Kort responstid
                </h2>
                <p className="text-slate-700 text-base leading-relaxed">
                  Vi kan stille med kvalificeret personale med kort varsel – også uden for normal arbejdstid. Vi har erfarne medarbejdere, der er vant til at håndtere:
                </p>

                <div className="bg-[#faf9f6] border border-[#eae6dd] rounded-xl p-6 sm:p-8">
                  <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                    {experiencedWith.map((item, idx) => (
                      <li key={idx} className="flex items-start gap-3 text-slate-800 text-sm sm:text-base">
                        <span className="w-5 h-5 rounded-full bg-[#b39366]/20 text-[#b39366] flex items-center justify-center text-xs shrink-0 mt-0.5 font-bold">
                          <i className="fa-solid fa-check text-[10px]" aria-hidden="true"></i>
                        </span>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* Sektion 4: Hvordan foregår det? */}
              <div className="space-y-4">
                <h2 className="text-2xl sm:text-3xl font-bold text-[#041c2c] tracking-tight">
                  Hvordan foregår det?
                </h2>
                <p className="text-slate-700 text-base sm:text-lg leading-relaxed">
                  Når vi bliver kontaktet om en akut opgave, afklarer vi hurtigt situationen og finder de rette medarbejdere til opgaven. Vi lægger vægt på en rolig og professionel tilgang fra første kontakt, så både kommune, samarbejdspartnere og ikke mindst borgeren selv oplever tryghed midt i en svær situation.
                </p>
              </div>

              {/* Sektion 5: Hvem henvender det sig til? */}
              <div className="space-y-4">
                <h2 className="text-2xl sm:text-3xl font-bold text-[#041c2c] tracking-tight">
                  Hvem henvender det sig til?
                </h2>
                <p className="text-slate-700 text-base sm:text-lg leading-relaxed">
                  Akut anbringelse bruges typisk af kommuner, der står med en sag, hvor en borger har akut brug for et andet sted at bo eller opholde sig – ofte i forbindelse med sammenbrud i en eksisterende foranstaltning, akut krise i hjemmet eller andre uforudsete hændelser.
                </p>
              </div>

              {/* Sektion 6: Dækningsområde */}
              <div className="bg-[#f8f7f4] border-l-4 border-[#b39366] rounded-r-xl p-6 space-y-2">
                <h2 className="text-xl font-bold text-[#041c2c]">
                  Dækningsområde
                </h2>
                <p className="text-slate-700 text-base leading-relaxed">
                  I modsætning til vores øvrige ydelser er akut anbringelse ikke begrænset til Jylland og Fyn. Vi modtager borgere fra hele landet og kan koordinere en løsning, uanset hvor i Danmark opgaven opstår.
                </p>
              </div>

              {/* CMS Galleri for Akut Anbringelse */}
              <div className="pt-6 border-t border-slate-200">
                <CmsGallery
                  gallerySlug="akut-anbringelse"
                  sectionTitle="Billeder fra akut indsats og tryghed"
                  sectionSubtitle="Se eksempler på vores professionelle og nærværende tilgang i akutte situationer."
                />
              </div>
            </div>

            {/* Sidebar Column */}
            <div className="lg:col-span-4 space-y-8">
              {/* CTA callout */}
              <div className="bg-[#041c2c] text-white rounded-2xl p-6 sm:p-8 space-y-4 sticky top-24">
                <h3 className="text-xl font-bold text-white">
                  Har I brug for akut anbringelse?
                </h3>
                <p className="text-slate-300 text-sm sm:text-base">
                  Vi står klar med kort varsel, døgnet rundt – i hele landet.
                </p>
                <div className="flex flex-col gap-3 pt-2">
                  <a
                    href="tel:+4523964042"
                    className="inline-flex items-center justify-center gap-2 bg-[#b39366] hover:bg-[#a18155] text-white px-4 py-2.5 rounded font-semibold text-sm transition-all whitespace-nowrap"
                  >
                    <i className="fa-solid fa-phone text-xs" aria-hidden="true"></i>
                    <span>Ring (+45) 23 96 40 42</span>
                  </a>
                  <a
                    href="mailto:kontakt@akutvikaren.dk"
                    className="inline-flex items-center justify-center gap-2 bg-slate-800 hover:bg-slate-700 text-white px-4 py-2.5 rounded font-medium text-sm transition-all whitespace-nowrap"
                  >
                    <i className="fa-solid fa-envelope text-xs" aria-hidden="true"></i>
                    <span>kontakt@akutvikaren.dk</span>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
